import useServiceDialog from './useServiceDialog';
import { parseJwt } from '../../parseJwt';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FaArrowRight, FaCalendarAlt, FaCheckCircle, FaClipboardList, FaClock, FaHourglassHalf, FaTimes } from 'react-icons/fa';
import api from '../Axios/axios';
import TechnicalServicePersonnelShell, { getUserInfoFromToken } from './TechnicalServicePersonnelShell';
import './TechnicalService.scss';

const SERVICE_TYPES = [
  { value: '1', label: 'Bak\u0131m' },
  { value: '2', label: 'Par\u00e7a De\u011fi\u015fimi' },
  { value: '3', label: 'Ar\u0131za Giderme' },
  { value: '4', label: 'Montaj' },
  { value: '5', label: 'S\u00f6k\u00fcm' },
  { value: '6', label: 'Kontrol' },
  { value: '7', label: 'Di\u011fer' }
];

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_META = {
  1: { label: 'Yeni Talep', className: 'new' },
  2: { label: 'Atand\u0131', className: 'assigned' },
  3: { label: '\u0130\u015flemde', className: 'progress' },
  4: { label: 'Tamamland\u0131', className: 'success' },
  5: { label: 'M\u00fcd\u00fcr Onay\u0131 Bekliyor', className: 'warning' },
  6: { label: 'M\u00fcd\u00fcr Onaylad\u0131', className: 'success' },
  7: { label: 'Muhasebeye Aktar\u0131ld\u0131', className: 'accounting' },
  8: { label: '\u0130ptal', className: 'cancelled' }
};

const PRIORITY_META = {
  urgent: { label: 'Acil', className: 'urgent' },
  normal: { label: 'Normal', className: 'normal' },
  low: { label: 'D\u00fc\u015f\u00fck', className: 'low' }
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const getStatusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META[1];

const getPriorityMeta = (job) => {
  const raw = String(job?.priority || job?.urgency || job?.importance || job?.subject || '').toLocaleLowerCase('tr-TR');
  if (raw.includes('acil') || raw.includes('urgent')) return PRIORITY_META.urgent;
  if (raw.includes('d\u00fc\u015f\u00fck') || raw.includes('dusuk') || raw.includes('low')) return PRIORITY_META.low;
  return PRIORITY_META.normal;
};

const getServiceTypeValue = (job) => {
  const rawValue =
    job?.serviceType ??
    job?.serviceTypeId ??
    job?.serviceTypeCode ??
    job?.technicalServiceType ??
    job?.technicalServiceTypeId;

  const numericValue = Number(rawValue);
  if (Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 7) {
    return String(numericValue);
  }

  const normalizedText = String(job?.serviceTypeName || job?.subject || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalizedText.includes('par\u00e7a')) return '2';
  if (normalizedText.includes('ar\u0131za')) return '3';
  if (normalizedText.includes('montaj')) return '4';
  if (normalizedText.includes('s\u00f6k\u00fcm')) return '5';
  if (normalizedText.includes('kontrol')) return '6';
  if (normalizedText.includes('di\u011fer')) return '7';
  if (normalizedText.includes('bak\u0131m')) return '1';

  return '1';
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const formatServiceDisplay = (job) => {
  const matched = SERVICE_TYPES.find((serviceType) => serviceType.value === getServiceTypeValue(job));
  return matched?.label || String(job?.subject || '-');
};

const toDateTimeLocalValue = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return '';

  try {
    const decoded = parseJwt(token);
    return (
      decoded.sub ||
      decoded.nameid ||
      decoded.userId ||
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
      ''
    );
  } catch (error) {
    console.error('Token parse edilemedi:', error);
    return '';
  }
};

const createEmptyForm = () => ({
  requestId: '',
  serviceType: '1',
  processDate: toDateTimeLocalValue(),
  machineSerialNo: '',
  description: '',
  serviceFormFiles: [],
  files: []
});

const TechnicalServiceMyJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('assigned');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [workLogForm, setWorkLogForm] = useState(createEmptyForm());
  const [messageBox, setMessageBox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const workFormRef = useRef(null);
  const currentUser = useMemo(() => getUserInfoFromToken(), []);

  const fetchJobs = useCallback(async () => {
    const userId = getUserIdFromToken();
    const response = await api.get('/TechnicalService/requests/my', {
      params: userId ? { userId } : {}
    });

    const items = response?.data?.data || [];
    setJobs(items);
    return items;
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await fetchJobs();
      } catch (error) {
        console.error('Atanan i\u015fler al\u0131namad\u0131:', error);
        setMessageBox({ type: 'error', title: 'Veri Al\u0131namad\u0131', message: 'Atanan i\u015fler y\u00fcklenemedi.' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [fetchJobs]);

  const normalizedJobs = useMemo(
    () =>
      jobs.map((job) => ({
        ...job,
        normalizedStatus: normalizeStatus(job.status),
        statusMeta: getStatusMeta(job.status),
        priorityMeta: getPriorityMeta(job),
        machineLabel: job.towerCraneSerialNo || job.externalMachineName || '-',
        workSiteLabel: job.workSiteName || job.externalWorkSiteName || '-'
      })),
    [jobs]
  );

  const activeJobs = normalizedJobs.filter((job) => job.normalizedStatus < 4);
  const completedCount = normalizedJobs.filter((job) => job.normalizedStatus >= 4).length;
  const inProgressJobs = activeJobs.filter((job) => job.normalizedStatus === 3);
  const assignedJobs = activeJobs.filter((job) => job.normalizedStatus !== 3);
  const visibleJobs = activeTab === 'progress' ? inProgressJobs : assignedJobs;
  const selectedJob = activeJobs.find((job) => String(job.id) === selectedJobId);
  const upcomingJobs = activeJobs
    .filter((job) => job.operationDate)
    .slice()
    .sort((a, b) => new Date(a.operationDate) - new Date(b.operationDate))
    .slice(0, 3);
  const shellNotifications = assignedJobs.slice(0, 5).map((job) => ({
    id: job.id,
    title: job.priorityMeta.className === 'urgent' ? 'Acil i\u015f atand\u0131' : 'Yeni i\u015f atand\u0131',
    description: `${job.subject} - ${formatDate(job.operationDate)}`
  }));

  const openWorkForm = (job) => {
    setSelectedJobId(String(job.id));
    setWorkLogForm({
      ...createEmptyForm(),
      requestId: String(job.id),
      serviceType: getServiceTypeValue(job),
      machineSerialNo: job.towerCraneSerialNo || ''
    });
  };

  const closeWorkForm = useCallback(() => {
    setSelectedJobId('');
    setWorkLogForm(createEmptyForm());
  }, []);
  useServiceDialog(workFormRef, Boolean(selectedJob), closeWorkForm);

  const handlePhotoFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setWorkLogForm((prev) => ({
      ...prev,
      files: [...prev.files, ...selectedFiles]
    }));
    event.target.value = '';
  };

  const handleServiceFormFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setWorkLogForm((prev) => ({
      ...prev,
      serviceFormFiles: [...prev.serviceFormFiles, ...selectedFiles]
    }));
    event.target.value = '';
  };

  const removeFile = (fileToRemove, fieldName = 'files') => {
    setWorkLogForm((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter(
        (file) => !(file.name === fileToRemove.name && file.lastModified === fileToRemove.lastModified)
      )
    }));
  };

  const submitWorkLog = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      if (!workLogForm.machineSerialNo.trim()) {
        throw new Error('Makinenin seri numaras\u0131 girilmelidir.');
      }

      if (workLogForm.serviceFormFiles.length === 0) {
        throw new Error('Teknik servis formu foto\u011fraf\u0131 y\u00fcklenmeden i\u015flem tamamlanamaz.');
      }

      const formData = new FormData();
      formData.append('requestId', workLogForm.requestId);
      formData.append('serviceType', workLogForm.serviceType);
      formData.append('processDate', workLogForm.processDate ? `${workLogForm.processDate}:00` : '');
      formData.append('machineSerialNo', workLogForm.machineSerialNo.trim());
      formData.append('description', workLogForm.description);
      formData.append('isCompleted', 'true');

      workLogForm.serviceFormFiles.forEach((file, index) => {
        formData.append(`files[${index}].file`, file);
        formData.append(`files[${index}].fileType`, '3');
      });

      workLogForm.files.forEach((file, index) => {
        const fileIndex = workLogForm.serviceFormFiles.length + index;
        formData.append(`files[${fileIndex}].file`, file);
        formData.append(`files[${fileIndex}].fileType`, '4');
      });

      await api.post('/TechnicalService/requests/work-log', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await fetchJobs();
      closeWorkForm();
      setMessageBox({
        type: 'success',
        title: '\u0130\u015flem Tamamland\u0131',
        message: '\u0130\u015flem m\u00fcd\u00fcr onay\u0131na g\u00f6nderildi.'
      });
    } catch (error) {
      console.error('\u0130\u015f kayd\u0131 g\u00f6nderilemedi:', error);
      setMessageBox({
        type: 'error',
        title: '\u0130\u015flem Tamamlanamad\u0131',
        message:
          error?.response?.data?.message ||
          error?.response?.data?.messages?.join(' ') ||
          error?.message ||
          '\u0130\u015f kayd\u0131 g\u00f6nderilemedi.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TechnicalServicePersonnelShell
      title={`Merhaba, ${currentUser.name}`}
      notificationCount={assignedJobs.length}
      notifications={shellNotifications}
      shellClassName="service-field-operator-shell"
    >
      {messageBox && (
        <div className="service-message-backdrop" role="presentation">
          <div className={`service-message-modal ${messageBox.type}`} role="alertdialog">
            <button type="button" onClick={() => setMessageBox(null)} aria-label="Kapat">
              <FaTimes />
            </button>
            <h3>{messageBox.title}</h3>
            <p>{messageBox.message}</p>
            <div>
              <button type="button" onClick={() => setMessageBox(null)}>Tamam</button>
            </div>
          </div>
        </div>
      )}

      <section className="service-personnel-dashboard service-jobs-dashboard">
        <div className="service-panel service-jobs-panel">
          <div className="service-section-title">
            <div>
              <h2>{'\u0130\u015flerim'}</h2>
              <p>{'İşini seç, formu doldur ve gönder.'}</p>
            </div>

          </div>

          <div className="service-job-tabs">
            <button type="button" className={activeTab === 'assigned' ? 'active' : ''} onClick={() => setActiveTab('assigned')}>
              {'Atanan \u0130\u015fler'} <span>{assignedJobs.length}</span>
            </button>
            <button type="button" className={activeTab === 'progress' ? 'active' : ''} onClick={() => setActiveTab('progress')}>
              {'Devam Edenler'} <span>{inProgressJobs.length}</span>
            </button>
          </div>

          {loading ? (
            <p>{'Atanan i\u015fler y\u00fckleniyor...'}</p>
          ) : visibleJobs.length > 0 ? (
            <div className="service-job-list">
              {visibleJobs.map((job) => (
                <article key={job.id} className="service-job-row clean field-job-row">
                  <div className="service-job-info field-job-info">
                    <span>{job.requestNo || `Talep #${job.id}`}</span>
                    <strong>{formatServiceDisplay(job)}</strong>
                    <small>{`Firma: ${job.requestingCompanyName || '-'}`}</small>
                    <small>{`Konum: ${job.workSiteLocation || job.workSiteLabel || '-'}`}</small>
                  </div>
                  <div className="service-priority-cell field-priority-cell">
                    <span className={`priority-pill ${job.priorityMeta.className}`}>{job.priorityMeta.label}</span>
                  </div>
                  <div className="service-job-date field-job-date">
                    <FaCalendarAlt />
                    <div>
                      <strong>Planlanan</strong>
                      <span>{formatDate(job.operationDate)}</span>
                    </div>
                  </div>
                  <button type="button" className="service-job-open field-job-open" onClick={() => openWorkForm(job)}>
                    {'İşi Aç'}
                    <FaArrowRight />
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <p>{'Bu sekmede g\u00f6sterilecek aktif i\u015f bulunmuyor.'}</p>
          )}
        </div>

        <aside className="service-personnel-aside">
          <section className="service-panel service-summary-panel">
            <h3>{'\u00d6zet Bilgiler'}</h3>
            <div className="service-summary-list">
              <div>
                <span><i className="summary-icon blue"><FaClipboardList /></i>{'Toplam \u0130\u015f'}</span>
                <strong>{jobs.length}</strong>
              </div>
              <div>
                <span><i className="summary-icon green"><FaClock /></i>{'Devam Eden'}</span>
                <strong>{inProgressJobs.length}</strong>
              </div>
              <div>
                <span><i className="summary-icon yellow"><FaCheckCircle /></i>{'Tamamlanan'}</span>
                <strong>{completedCount}</strong>
              </div>
              <div>
                <span><i className="summary-icon red"><FaHourglassHalf /></i>{'Bekleyen'}</span>
                <strong>{assignedJobs.length}</strong>
              </div>
            </div>
          </section>

          <section className="service-panel service-upcoming-panel">
            <h3>{'Yakla\u015fan \u0130\u015fler'}</h3>
            {upcomingJobs.length > 0 ? (
              upcomingJobs.map((job) => (
                <button key={job.id} type="button" onClick={() => openWorkForm(job)}>
                  <strong>{formatServiceDisplay(job)}</strong>
                  <span>{formatDate(job.operationDate)}</span>
                </button>
              ))
            ) : (
              <p>{'Planlanm\u0131\u015f aktif i\u015f bulunmuyor.'}</p>
            )}
          </section>
        </aside>
      </section>

      {selectedJob && (
        <div className="service-workform-backdrop">
          <section className="service-panel service-workform-dialog service-simple-workform" ref={workFormRef} role="dialog" aria-modal="true" aria-labelledby="service-workform-title" tabIndex={-1}>
            <div className="service-workform-header">
              <div>
                <button type="button" onClick={closeWorkForm}>{'← İşlerime dön'}</button>
                <h2 id="service-workform-title">Servis Formu</h2>
                <p>{selectedJob.requestNo || `Talep #${selectedJob.id}`} · {formatServiceDisplay(selectedJob)}</p>
              </div>
              <span className={`badge ${selectedJob.statusMeta.className}`}>{selectedJob.statusMeta.label}</span>
            </div>

            <details className="service-job-extra"><summary>İş bilgileri</summary>
            <div className="job-summary service-selected-summary">
              <div><strong>Firma:</strong> {selectedJob.requestingCompanyName || '-'}</div>
              <div><strong>Makine:</strong> {selectedJob.machineLabel}</div>
              <div><strong>{'\u015eantiye:'}</strong> {selectedJob.workSiteLabel}</div>
              <div><strong>Konum:</strong> {selectedJob.workSiteLocation || '-'}</div>
              <div><strong>Planlanan:</strong> {formatDate(selectedJob.operationDate)}</div>
              <div><strong>Konu:</strong> {selectedJob.subject}</div>
            </div>

            </details>

            <form className="technical-service-form service-workform" onSubmit={submitWorkLog}>
              <section className="service-form-step">
                <h3>1. Yapılan işi yaz</h3>
                <div className="field-grid">
                  <label>
                    Makine seri numarası
                    <input value={workLogForm.machineSerialNo}
                      onChange={(event) => setWorkLogForm((prev) => ({ ...prev, machineSerialNo: event.target.value }))}
                      placeholder="Makinenin seri numarası" required />
                  </label>
                  <label>
                    Ne yaptınız?
                    <textarea rows="3" value={workLogForm.description}
                      onChange={(event) => setWorkLogForm((prev) => ({ ...prev, description: event.target.value }))}
                      placeholder="Örn: Bağlantıları kontrol ettim, arızalı parçayı değiştirdim." required />
                  </label>
                </div>
                <details className="service-form-extra">
                  <summary>Servis türü ve işlem tarihi</summary>
                  <div className="field-grid">
                    <label>Servis türü
                      <select value={workLogForm.serviceType}
                        onChange={(event) => setWorkLogForm((prev) => ({ ...prev, serviceType: event.target.value }))}>
                        {SERVICE_TYPES.map((serviceType) => (
                          <option key={serviceType.value} value={serviceType.value}>{serviceType.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>İşlem tarihi
                      <input type="datetime-local" value={workLogForm.processDate}
                        onChange={(event) => setWorkLogForm((prev) => ({ ...prev, processDate: event.target.value }))}
                        onInvalid={(event) => { event.target.closest('details').open = true; }} required />
                    </label>
                  </div>
                </details>
              </section>

              <div className="service-upload-grid">
                <div className="upload-section">
                  <div className="parts-header">
                    <h3>2. Servis formu fotoğrafını ekle</h3>
                    <strong>Zorunlu</strong>
                  </div>
                  <label className="upload-box required-upload-box">
                    <span>{'Fotoğraf çek / seç'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleServiceFormFileChange}
                      required={workLogForm.serviceFormFiles.length === 0}
                    />
                  </label>
                  {workLogForm.serviceFormFiles.length > 0 && (
                    <div className="file-pill-row">
                      {workLogForm.serviceFormFiles.map((file) => (
                        <button
                          key={`service-form-${file.name}-${file.lastModified}`}
                          type="button"
                          className="file-pill removable"
                          onClick={() => removeFile(file, 'serviceFormFiles')}
                        >
                          {file.name} · Kaldır
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="upload-section">
                  <details className="service-form-extra"><summary>Makine fotoğrafı ekle (isteğe bağlı)</summary>
                  <label className="upload-box">
                    <span>{'Makine foto\u011fraf\u0131 \u00e7ek / y\u00fckle'}</span>
                    <input type="file" accept="image/*" capture="environment" multiple onChange={handlePhotoFileChange} />
                  </label>
                  {workLogForm.files.length > 0 && (
                    <div className="file-pill-row">
                      {workLogForm.files.map((file) => (
                        <button
                          key={`${file.name}-${file.lastModified}`}
                          type="button"
                          className="file-pill removable"
                          onClick={() => removeFile(file)}
                        >
                          {file.name}
                        </button>
                      ))}
                    </div>
                  )}
                  </details>
                </div>
              </div>

              <div className="service-workform-actions">
                <button type="submit" className="primary-btn" disabled={submitting}>
                  {submitting ? 'Kaydediliyor...' : 'Tamamla ve Gönder'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </TechnicalServicePersonnelShell>
  );
};

export default TechnicalServiceMyJobsPage;
